import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useAPI from '../../../../../hooks/useAPI';

import SUTextField from '../../Components/SUTextField';
import NodeEditorChrome from '../../Components/NodeEditorChrome';
import { NODE_THEMES } from '../../Components/NodeTheme';
import TriggerTypeField from '../../Components/TriggerTypeField';
import useApprovedTemplates from '../../Components/useApprovedTemplates';
import useNodeEditor from '../../Components/useNodeEditor';
import { InfoCallout } from '../../Components/NodeFormKit';
import ValueFiltersEditor from '../../Components/ValueFiltersEditor';
import { getFlowsWildcardError } from '../../Components/flowTriggerRules';

import styledComponent from 'styled-components';
import Select from 'react-select';
import { ToastContainer } from 'react-toastify';

import { BsGearFill } from 'react-icons/bs';
import { IoIosArrowDropdown, IoIosArrowDropup } from 'react-icons/io';
import { FaDatabase, FaEdit, FaEraser } from 'react-icons/fa';
import { HiOutlineSave } from 'react-icons/hi';

const SAVE_ANSWER_NODE_COLOR = '#D89C7E';

const SaveAnswer = ({
  selectedNode,
  handleSelectedNode,
  removeNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  truncateText,
  customStyles,
  customStylesDark,
  isDark,
  correspondence,
  watchSelectedNode,
  onMouseDown,
  isDragging,
  isExpanded,
  setIsExpanded,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    setError,
  } = useForm({
    user_response: null,
  });

  const { templates, loading: loadingTemplates } = useApprovedTemplates();
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;

  const { urlAPI_2, fetchWithAuth } = useAPI();

  const { handleInputChange, loadData, saveNode, getIsDirty } = useNodeEditor({
    selectedNode,
    handleSelectedNode,
    nodes,
    edges,
    editFlow,
    updateBot,
    reset,
    getValues,
  });

  const [errorVariable, setErrorVariable] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  // ------> Dropdowns
  const [dropdownRegularExpression, setDropdownRegularExpression] =
    useState(false);

  // FUNCTIONS


  const handleDropdown = (property) => {
    switch (property) {
      case 'dropdownRegularExpression':
        setDropdownRegularExpression(!dropdownRegularExpression);
        break;
      default:
        console.error(`Property ${property} not recognized`);
    }
  };


  const saveData = (values) => {
    if (!selectedNode.data.variable || !selectedNode.data.variable.length) {
      setErrorVariable(true);
      return;
    }
    const flowsWildcardError = getFlowsWildcardError(
      selectedNode.data?.trigger_type,
      values.user_response,
      selectedNode.data?.flow_template_id
    );
    if (flowsWildcardError) {
      setError('user_response', { type: 'manual', message: flowsWildcardError });
      return;
    }

    setLoadingUpdate(true);

    const data = {
      title: values.title,
      user_response: values.user_response,
      trigger_type: selectedNode.data?.trigger_type || 'message',
      flowResponseOrder: values.flow_response_order,
      advanced: {
        ...selectedNode.data?.advanced,
        regularExpressions: {
          ...selectedNode.data?.advanced?.regularExpressions,
          errorMessage: values.regular_expressions_error_message,
        },
      },
    };

    saveNode(data, () => {
      setLoadingUpdate(false);
    });
  };

  const getData = () => {
    let objectData = {
      title: selectedNode.data?.title ?? null,
      user_response: selectedNode.data?.user_response ?? null,
      trigger_type: selectedNode.data?.trigger_type ?? 'message',
      flow_response_order: selectedNode.data?.flowResponseOrder,
      regular_expressions_error_message:
        selectedNode.data?.advanced?.regularExpressions?.errorMessage ?? null,
    };
    loadData(objectData);
  };

  useEffect(() => {
    getData();
  }, [watchSelectedNode]);

  // Manejador específico para actualizar propiedades individuales de una variable (source, manualValue)
  const updateVariableConfig = (index, key, value) => {
    const currentVars = [...(selectedNode.data.variable || [])];
    currentVars[index] = {
      ...currentVars[index],
      [key]: value
    };
    handleInputChange('variable', currentVars);
  };

  return (
    <NodeEditorChrome
      icon={HiOutlineSave}
      color={SAVE_ANSWER_NODE_COLOR}
      typeName="Guardar Respuesta"
      title={truncateText(selectedNode.data.title)}
      onRemove={() => removeNode(selectedNode.id)}
      onMouseDown={onMouseDown}
      isDragging={isDragging}
      onClose={() => handleSelectedNode(null)}
      formId="saveAnswerForm"
      saving={loadingUpdate}
      isDark={isDark}
      getIsDirty={getIsDirty}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      toast={<ToastContainer theme={isDark === 'true' ? 'dark' : 'light'} />}
    >
        <form id="saveAnswerForm" onSubmit={handleSubmit(saveData)} style={{ width: '100%' }}>
          <div>
            <div className="col p-0">
              <SUTextField
                id="title"
                name="title"
                label="Título:"
                type="text"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('title', {
                  required: { value: true, message: 'Campo requerido.' },
                  pattern: { value: /\S+/, message: 'No puede ser solo espacios en blanco' },
                })}
              />
            </div>
            
            <div className="col p-0 mt-4">
              <TriggerTypeField
                value={selectedNode.data?.trigger_type}
                handleInputChange={handleInputChange}
                isDark={isDark}
                customStyles={customStyles}
                customStylesDark={customStylesDark}
              />
            </div>

            {selectedNode.data?.trigger_type === 'flows' && (
              <div className="col p-0 mt-4">
                <label htmlFor="flow_template_id" style={{ color: theme.textSecondary, fontWeight: 600 }}>
                  Plantilla esperada (opcional):
                </label>
                <div className="su-control-group d-flex">
                  <Select
                    styles={isDark == 'true' ? customStylesDark : customStyles}
                    className="form-control basic-multi-select"
                    placeholder={loadingTemplates ? 'Cargando plantillas...' : 'Seleccione una plantilla'}
                    name="flow_template_id"
                    value={
                      templates.find(
                        (templateOption) =>
                          String(templateOption.value) === String(selectedNode.data?.flow_template_id || '')
                      ) || null
                    }
                    onChange={(option) => {
                      handleInputChange('flow_template_id', option ? String(option.value) : null);
                      handleInputChange('flow_template_name', option ? option.label : null);
                    }}
                    options={templates}
                    isClearable
                    isLoading={loadingTemplates}
                    classNamePrefix="select"
                    noOptionsMessage={() => 'Sin opciones'}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                  />
                </div>
                <InfoCallout>
                  Si seleccionas una plantilla, este nodo tipo flujo solo se
                  activará cuando el usuario responda a esa plantilla. Con * (cualquier respuesta) la plantilla es obligatoria y el nodo solo se activa dentro de las 24 h siguientes a su envío.
                </InfoCallout>
              </div>
            )}

            <div className="col p-0 mt-4">
              <SUTextField
                id="user_response"
                name="user_response"
                label="Respuesta del cliente:"
                hint="Lo que debe llegar para activar este nodo: el texto que escribe el cliente, el título de un botón, o * para cualquier respuesta. Para varios, separalos con  /  ."
                type="textArea"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('user_response', {
                  required: { value: true, message: 'Campo requerido.' },
                })}
              />
            </div>

            <div className="col p-0 mt-4">
              <label>Variable / Destino: </label>
              <div className="su-control-group d-flex">
                <Select
                  styles={isDark == 'true' ? customStylesDark : customStyles}
                  isMulti
                  className="form-control basic-multi-select"
                  placeholder="Seleccione..."
                  name="variables"
                  value={selectedNode.data.variable}
                  onChange={(val) => {
                    // Mapeamos para asegurar que cada variable tenga su estructura de configuración
                    const formatted = val.map(v => ({
                      ...v,
                      source: v.source || 'user_message',
                      manualValue: v.manualValue || ''
                    }));
                    handleInputChange('variable', formatted);
                    setErrorVariable(false);
                  }}
                  options={correspondence}
                  classNamePrefix="select"
                  getOptionLabel={(option) => option.fieldName}
                  getOptionValue={(option) => option.fieldName}
                />
              </div>
              {errorVariable && <Alert>* Campo requerido</Alert>}

              {/* CONFIGURACIÓN DE ASIGNACIÓN MODERNA */}
              {selectedNode.data.variable?.length > 0 && (
                <div className="mt-3">
                  <div className="d-flex align-items-center mb-3" style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: isDark == 'true' ?'1px solid #2a3942' : '1px solid #e3e9ec'
                  }}>
                    <BsGearFill className="mr-2" style={{ color: '#D89C7E', fontSize: '16px' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: theme.textPrimary, letterSpacing: '0.5px' }}>
                      CONFIGURACIÓN DE ASIGNACIÓN
                    </span>
                  </div>

                  <div className="row g-2">
                    {selectedNode.data.variable.map((v, index) => {
                      const getSourceIcon = (source) => {
                        switch(source) {
                          case 'user_message': return <FaDatabase style={{ color: '#2fae4e', fontSize: '14px' }} />;
                          case 'manual': return <FaEdit style={{ color: '#0a7ba5', fontSize: '14px' }} />;
                          case 'clear': return <FaEraser style={{ color: '#FF2242', fontSize: '14px' }} />;
                          default: return <FaDatabase style={{ color: '#2fae4e', fontSize: '14px' }} />;
                        }
                      };

                      return (
                        <div key={index} className="col-12 mb-3">
                          <div className="card h-100" style={{
                            border: isDark == 'true' ?'1px solid #2a3942' : '1px solid #e3e9ec',
                            borderRadius: '12px',
                            backgroundColor: isDark == 'true' ?'#111b21' : '#ffffff',
                            boxShadow: isDark == 'true' ?'0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)'
                          }}>
                            <div className="card-body p-3">
                              {/* Header de la variable */}
                              <div className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: isDark == 'true' ?'1px solid #2a3942' : '1px solid #f1f3f4' }}>
                                <div className="d-flex align-items-center flex-grow-1">
                                    <div className="mr-2" style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: '#D89C7E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <BsGearFill style={{ color: 'white', fontSize: '14px' }} />
                                    </div>
                                    <div>
                                      <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: 600, color: theme.textPrimary }}>{v.fieldName}</h6>
                                      <small style={{ fontSize: '12px', color: theme.textSecondary }}>Campo de destino</small>
                                    </div>
                                </div>
                              </div>

                              {/* Selección de Origen */}
                              <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: 600, color: theme.textSecondary }}>Origen del dato:</label>
                                <Select
                                  styles={isDark == 'true' ?customStylesDark : customStyles}
                                  value={[
                                    { label: 'Guardar respuesta del cliente', value: 'user_message' },
                                    { label: 'Asignar valor fijo manual', value: 'manual' },
                                    { label: 'Limpiar campo (Vaciar / Null)', value: 'clear' }
                                  ].find(option => option.value === v.source)}
                                  onChange={(option) => updateVariableConfig(index, 'source', option.value)}
                                  options={[
                                    { label: 'Guardar respuesta del cliente', value: 'user_message' },
                                    { label: 'Asignar valor fijo manual', value: 'manual' },
                                    { label: 'Limpiar campo (Vaciar / Null)', value: 'clear' }
                                  ]}
                                  formatOptionLabel={(option) => (
                                    <div className="d-flex align-items-center" style={{ fontSize: '12px' }}>
                                      <span className="mr-2">{getSourceIcon(option.value)}</span>
                                      <span>{option.label}</span>
                                    </div>
                                  )}
                                />
                              </div>

                              {v.source === 'manual' && (
                                <div className="mb-3">
                                  <input 
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Valor fijo a asignar..."
                                    value={v.manualValue}
                                    onChange={(e) => updateVariableConfig(index, 'manualValue', e.target.value)}
                                  />
                                </div>
                              )}

                              <ValueFiltersEditor
                                filters={v.filters || []}
                                onChange={(filters) => updateVariableConfig(index, 'filters', filters)}
                                isDark={isDark}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <p style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 800 }} className="pt-2">
                Ejecuta la acción y salta al siguiente nodo.
              </p>
            </div>

            {/* RESTO DEL FORMULARIO (EXPRESIONES REGULARES Y FLUJOS) */}
            <div className="d-flex align-items-center mb-3 mt-4">
              <input
                name="regularExpressions"
                id="regularExpressions"
                type="checkbox"
                checked={selectedNode.data?.advanced?.regularExpressions?.active || false}
                className="cmn-toggle cmn-toggle-round-flat"
                onChange={(e) => handleInputChange('advanced.regularExpressions.active', e.target.checked)}
              />
              <label className="mr-2 mb-0" htmlFor="regularExpressions"></label>
              <div style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 600 }}>Expresiones regulares</div>
            </div>

            {selectedNode.data?.advanced?.regularExpressions?.active && (
              <div className="col p-0 mt-2 mb-4 border rounded p-2">
                  <div 
                    className="d-flex justify-content-between align-items-center pointer"
                    onClick={() => handleDropdown('dropdownRegularExpression')}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>Configuración de Validación</span>
                    {dropdownRegularExpression ? <IoIosArrowDropup /> : <IoIosArrowDropdown />}
                  </div>

                  {dropdownRegularExpression && (
                    <div className="mt-3">
                        <label style={{ fontSize: '12px' }}>Tipo:</label>
                        <Select
                          styles={isDark == 'true' ? customStylesDark : customStyles}
                          value={selectedNode.data?.advanced?.regularExpressions?.type}
                          onChange={(val) => handleInputChange('advanced.regularExpressions.type', val)}
                          options={[
                            { label: 'Fecha', id: 'date' },
                            { label: 'Hora', id: 'hour' },
                            { label: 'Correo', id: 'mail' },
                            { label: 'Numérico', id: 'number' },
                            { label: 'Teléfono', id: 'phone' },
                          ]}
                          getOptionLabel={(o) => o.label}
                        />
                        <div className="mt-3">
                           <label style={{ fontSize: '12px' }}>Mensaje de error:</label>
                           <textarea
                              className="form-control"
                              style={{ fontSize: '12px' }}
                              {...register('regular_expressions_error_message')}
                           />
                        </div>
                    </div>
                  )}
              </div>
            )}
            
            <div className="d-flex align-items-center mb-1 mt-4">
              <input
                name="flowResponse"
                id="flowResponse"
                type="checkbox"
                checked={selectedNode.data?.flowResponse || false}
                className="cmn-toggle cmn-toggle-round-flat"
                onChange={(e) => handleInputChange('flowResponse', e.target.checked)}
              />
              <label className="mr-2 mb-0" htmlFor="flowResponse"></label>
              <div style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 600 }}>Respuesta de flujo</div>
            </div>
            {selectedNode.data?.flowResponse && (
              <div className="col p-0">
                <label
                  htmlFor="flowResponseOrder"
                  style={{ color: theme.textSecondary, fontWeight: 600 }}
                >
                  Orden de campos del flujo:
                </label>
                <div>
                  <textarea
                    className="form-control"
                    id="flowResponseOrder"
                    name="flowResponseOrder"
                    type="text"
                    style={{ width: "100%", height: "50px", borderRadius: "4px" }}
                    {...register("flow_response_order", {
                      required: {
                        value: selectedNode.data?.flowResponse ? true : false,
                        message: "Campo requerido.",
                      },
                      pattern: {
                        value: /\S+/, 
                        message: "No puede ser solo espacios en blanco",
                      },
                    })}
                  />
                  {errors.flow_response_order && <Alert>* {errors.flow_response_order.message}</Alert>}
                  <p style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 600 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>Importante: </span>
                    Los campos del flujo se deben ingresar en el orden en el que se quiere guardar, ejemplo: Campo 1 / Campo 2 / Campo 3. Estos campos deben tener el nombre exacto de como se configuraron en el flujo, de lo contrario no funcionará esta acción.
                  </p>
                </div>
              </div>
            )}
          </div>
        </form>
    </NodeEditorChrome>
  );
};

export default SaveAnswer;

const Alert = styledComponent.span`
  color: red;
  display: block;
  width: 100%;
  font-size: 12px;
`;